import {
  SelectOption,
  AttributeGroupDTO,
  AttributeGroupType,
  AttributeGroupValueDTO
} from '@touche-lts/panel-ui/dist'
import { makeAutoObservable } from 'mobx'
import RootStore  from 'mobx-stores/root-store'
import { ColorPickerUtil } from 'mobx-stores/stores/store-attributes-store/store-attributes-store.utils'
import { ProductSKUsDTO, SKUTableActions, SKUTableSortedBy } from 'models'
import { Api } from 'services'
import { generateLocalId, getSelectOptions, isLocalId } from 'utilities'

export class ProductsSKUsStore {
  private rootStore:any
  private productsApi: Api['productsApi']

  constructor (rootStore:any) {
    this.rootStore = rootStore
    this.productsApi = this.rootStore.generalStore.api.productsApi
    makeAutoObservable(this)
  }

  // OBSERVABLES
  private productSKUs: ProductSKUsDTO = {
    // TODO-A To check and fix if needed
    curr: 'USD',
    price: 0,
    sku: '',
    stock: 0
  }
  public editMode: boolean = false
  private productAttributes: AttributeGroupDTO[] = []
  private attributeGroupsBeforeChange: AttributeGroupDTO[] = []
  private initialAttributes: AttributeGroupDTO[] = []
  private columnAttributeIdSelected: number[] = []
  private allChecked: boolean = false;

  public selectedCheckboxes: Map<number, Partial<ProductSKUsDTO>> = new Map()
  public selectedTableAction: SKUTableActions = SKUTableActions.ACTIONS
  public selectedTableSortedBy: SKUTableSortedBy = SKUTableSortedBy.ALL
  public editSkuByAction: boolean = false
  public newSkuValueInput: string | number = null


  // ACTIONS
  public setProductSKUs = (productSKUs: ProductSKUsDTO): void => {
    this.productSKUs = productSKUs
  }

  public setProductSKUsTableInitial = (
    initialAttributes: Partial<AttributeGroupDTO[]>
  ): void => {
    this.initialAttributes = initialAttributes
  }

  public setProductSKUsProps = (props: Partial<ProductSKUsDTO>): void => {
    this.setProductSKUs({ ...this.productSKUs, ...props })
  }

  public setAllChecked = (checked: boolean) => {
    this.allChecked = checked;
  }

  public setEditModeSkuByAction = (): void => {
    if (!this.selectedCheckboxesLength) {
      this.resetApplySkuAction()
    }
  }

  public onCancelAction = (): void => {
    this.resetApplySkuAction()
  }

  public setNewValueFromAction = (newValue: string | number) => {
    this.newSkuValueInput = newValue
  }

  public setColumnAttributeSelected = (attrId: number, indexDynamicColumn: number) => {
    let attrArray = [...this.columnAttributeIdSelected];
    if(!attrArray.includes(attrId)){
      attrArray[indexDynamicColumn] = attrId;              
    } else{
      attrArray[indexDynamicColumn] = null
    }
    this.columnAttributeIdSelected = [...attrArray]
  }

  public resetApplySkuAction = (): void => {
    this.setEditSkuByAction(false)
    this.setTableAction(SKUTableActions.ACTIONS)
  }

  public clearSelectedCheckboxes = (): void => {
    this.selectedCheckboxes.clear()
  }

  public setTableSortedBy = (action: SKUTableSortedBy): void => {
    this.selectedTableSortedBy = action
    const status = action === SKUTableSortedBy.ALL ? null : action.toUpperCase()
    this.rootStore.productsSKUTableStore.onSearchPropsChange({ status })
  }

  private setTableAction = (action: SKUTableActions): void => {
    this.selectedTableAction = action
  }

  private setEditSkuByAction = (isActive: boolean): void => {
    this.editSkuByAction = isActive
  }

  private setProductAttributes (productArribute: AttributeGroupDTO[]) {
    this.productAttributes = productArribute;
    
    (this.productAttributes as any).forEach((attribute) => {
      if(!attribute.id) {
        attribute.id = 'dummy-' + Math.random().toString(36).slice(-5); 
      }
    });
  }

  private setEditMode = (value: boolean): void => {
    this.editMode = value
  }

  private getAttributeGroupById = (id: number): AttributeGroupDTO => {
    return this.productAttributes.find(attrGroup => attrGroup.id === id)
  }

  // COMPUTED
  public get productSKUsStore (): ProductSKUsDTO {
    return this.productSKUs
  }

  public get productSKUsAttributeStore (): AttributeGroupDTO[] {
    return this.productAttributes
  }

  public get haveAttributeGroups (): boolean {
    return !!this.productAttributes.length
  }

  public get hasInitialAttributesAttributes (): boolean {
    return !!this.initialAttributes?.length
  }

  public get attributeGroupTypeOptions (): SelectOption<AttributeGroupType>[] {
    return getSelectOptions(AttributeGroupType)
  }

  public get selectedCheckboxesLength (): number {
    return Array.from(this.selectedCheckboxes.values()).length
  }

  public get selectedCheckboxesKeys (): number[] {
    return Array.from(this.selectedCheckboxes.keys())
  }

  public get tableActionSelected (): SKUTableActions {
    return this.selectedTableAction
  }

  public get tableSortedBySelected (): SKUTableSortedBy {
    return this.selectedTableSortedBy
  }

  public get getAttributeIdSelected () {
    return this.columnAttributeIdSelected
  }

  public get allCheckedProducts (): boolean {
    return this.allChecked
  }

  // METHODS
  public selectTableAction = (
    action: SKUTableActions,
    productId: string | number
  ): void => {
    this.setTableAction(action)
    if (action === SKUTableActions.ARCHIVE) {
      this.rootStore.productsSKUTableStore.handleNewValueActionApply(productId)
      return
    }
    if (action !== SKUTableActions.ACTIONS) {
      this.setEditSkuByAction(true)
    } else {
      this.setEditSkuByAction(false)
    }
  }

  public updateProductSKUs = (productId: number | string) => {
    this.rootStore.viewProductsPageStore.updateProduct(
      { mainSku: { ...this.productSKUs } },
      productId
    )
  }

  public onRemoveAttributeClick = (attributeId: number): void => {
    this.setProductAttributes(
      this.productAttributes.filter(({ id }) => id !== attributeId)
    )
  }

  public removeAttributeValue = (
    attributeGroupId: number,
    attributeGroupValueId: number
  ): void => {
    const attributeGroup = this.getAttributeGroupById(attributeGroupId)
    if (!attributeGroup) return

    const attributeGroupValues = [...(attributeGroup.values || [])].filter(
      value => value.id !== attributeGroupValueId
    )
    this.setAttributeGroupProps(attributeGroupId, {
      values: attributeGroupValues
    })
  }

  public setAttributeGroupProps = (
    attributeGroupId: number,
    props: Partial<AttributeGroupDTO>
  ): void => {
    const attributeGroupIndex = this.productAttributes.findIndex(
      attrGroup => attrGroup.id === attributeGroupId
    )
    const attributeGroup = this.productAttributes[attributeGroupIndex]
    if (!attributeGroup) return
    const attributeGroupUpdated = { ...attributeGroup, ...props }
    const attributeGroupsUpdated = [...this.productAttributes]
    attributeGroupsUpdated[attributeGroupIndex] = attributeGroupUpdated
    this.setProductAttributes(attributeGroupsUpdated)
  }

  public getProductSKUAttributes = async (
    productId: number | string
  ): Promise<AttributeGroupDTO[]> => {
    const store = await this.productsApi.getProductAttributes(productId)
    if (!store) return
    this.setProductAttributes(store)
    this.setProductSKUsTableInitial(store)
  }

  public addAttributeValue = (
    attributeGroupId: number,
    attributeValue: string
  ): void => {
    const newAttributeValue: AttributeGroupValueDTO = {
      id: generateLocalId(),
      createdDate: null,
      name: attributeValue,
      status: null,
      value: attributeValue,
      groupId: attributeGroupId
    }
    const attributeGroup = this.getAttributeGroupById(attributeGroupId)
    if (!attributeGroup) return

    const applyNewAttributeValue = () => {
      const attributeGroupValues = [...(attributeGroup.values || [])]
      attributeGroupValues.push(newAttributeValue)
      this.setAttributeGroupProps(attributeGroupId, {
        values: attributeGroupValues
      })
    }

    const isColor = attributeGroup.type === AttributeGroupType.COLOR

    if (isColor) {
      const { openDialogPopup, closeDialogPopup } = this.rootStore.generalStore
      openDialogPopup({
        children: ColorPickerUtil({
          onChange: colorValue => {
            newAttributeValue.value = colorValue
          },
          // width: 590,
          initialValue: newAttributeValue.value
        }),
        preset: 'leftAligned',
        headingComponent: 'Numbers',
        heading: `Choose the value for "${attributeValue}" color`,
        closeButtonLabel: 'Cancel',
        buttons: [
          {
            preset: 'primary',
            children: 'Save',
            onClick: () => {
              applyNewAttributeValue()
              closeDialogPopup()
            }
          }
        ]
      })
    } else {
      applyNewAttributeValue()
    }
  }

  public updateProductSKUAttributes = async (
    productId: number | string
  ): Promise<AttributeGroupDTO[]> => {
    const attributeGroupsUpdate = [
      ...this.productAttributes.filter(({ name, type }) =>
        Boolean(name && type)
      )
    ]
    attributeGroupsUpdate.forEach(attributeGroup => {
      if (isLocalId(attributeGroup.id) || attributeGroup.id.toString().includes('dummy-')) {
        attributeGroup.id = null
      }
      attributeGroup.values.forEach(value => {
        if (isLocalId(value.id)) {
          value.id = null
        }
      })
    })

    const productAttributesStore = await this.productsApi.updateAttributeGroups(
      productId,
      attributeGroupsUpdate
    )
    if (!productAttributesStore) return

    this.productAttributes = [];
    this.setProductAttributes(productAttributesStore)
    this.setProductSKUsTableInitial(productAttributesStore)

    this.rootStore.productsSKUTableStore.triggerSearch({
      resetPageNumber: true
    })

    this.setEditMode(false)
    this.attributeGroupsBeforeChange = null
  }

  public onAddAttributeGroupClick = (): void => {
    const newAttributeGroup: AttributeGroupDTO = {
      id: generateLocalId(),
      createdDate: null,
      name: '',
      opt: null,
      status: null,
      type: null,
      values: []
    }

    const currentAttributeGroups = [...this.productAttributes]
    currentAttributeGroups.push(newAttributeGroup)

    this.setEditMode(true)
    if (!this.attributeGroupsBeforeChange) {
      this.attributeGroupsBeforeChange = this.productAttributes
    }
    this.setProductAttributes(currentAttributeGroups)
  }

  public onEditButtonClick = (): void => {
    this.attributeGroupsBeforeChange = this.productAttributes
    this.setEditMode(true)
  }

  public openDialogSKUs = (productId: number | string, productSKU: string) => {
    const {
      generalStore: { openDialogPopup, closeDialogPopup }
    } = this.rootStore

    if (!this.productAttributes.length) return

    openDialogPopup({
      preset: 'leftAligned',
      headingComponent: 'Numbers',
      heading: 'Generate SKUs',
      message: `SKUs will be generated automatically according to the new attributes. You will be able to change them manually. The purchase history for your existing product with SKU "${productSKU}" will not be lost.`,
      closeButtonLabel: 'Cancel',
      buttons: [
        {
          preset: 'primary',
          children: 'OK',
          onClick: () => {
            this.updateProductSKUAttributes(productId)
            closeDialogPopup()
          }
        }
      ]
    })
  }

  public onCancelEditClick = (): void => {
    this.setProductAttributes(this.attributeGroupsBeforeChange || [])
    this.attributeGroupsBeforeChange = null
    this.setEditMode(false)
  }

  InitProductSKUs = (): void => {
    this.setProductSKUs({
      curr: 'USD',
      price: 0,
      sku: '',
      stock: 0
    })
  }
}
